import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type BillingCategory,
  type CostUsageSummary,
  whatsappService,
} from "@/services/whatsapp.service/whatsapp.api";
import { AlertTriangle, Loader2, TrendingUp } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "sonner";

// ─── Constantes ────────────────────────────────────────────────────────────────

const PRICING_CHANGE_LABEL = "1º de outubro de 2026";

const CATEGORY_LABEL: Record<BillingCategory, string> = {
  marketing: "Marketing",
  utility: "Utilidade",
  authentication: "Autenticação",
  service: "Serviço (texto livre)",
};

const CATEGORY_HINT: Record<BillingCategory, string> = {
  marketing: "Templates promocionais",
  utility: "Notificações (ex.: chamados)",
  authentication: "Códigos de verificação",
  service: "Resposta livre na janela de 24h",
};

type PeriodKey = "this_month" | "last_month" | "last_30" | "last_90";

const PERIODS: Array<{ key: PeriodKey; label: string }> = [
  { key: "this_month", label: "Este mês" },
  { key: "last_month", label: "Mês passado" },
  { key: "last_30", label: "Últimos 30 dias" },
  { key: "last_90", label: "Últimos 90 dias" },
];

function getRange(period: PeriodKey): { from: Date; to: Date } {
  const now = new Date();
  if (period === "last_month") {
    return {
      from: new Date(now.getFullYear(), now.getMonth() - 1, 1),
      to: new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59),
    };
  }
  if (period === "last_30") return { from: new Date(now.getTime() - 30 * 86400000), to: now };
  if (period === "last_90") return { from: new Date(now.getTime() - 90 * 86400000), to: now };
  return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: now };
}

function formatBRL(value: number): string {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

const tooltipStyle = {
  borderRadius: "0.75rem",
  border: "1px solid var(--border)",
  backgroundColor: "var(--card)",
  boxShadow: "0 4px 24px 0 rgba(0,0,0,0.08)",
  fontSize: "13px",
};

// ─── Stat card ──────────────────────────────────────────────────────────────

function StatCard({
  title,
  value,
  description,
  icon,
  accent,
}: {
  title: string;
  value: string;
  description: string;
  icon?: React.ReactNode;
  accent?: "warning";
}) {
  return (
    <div className="rounded-2xl border border-border/50 bg-card p-6 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
          <h3
            className={`mt-2 text-3xl font-extrabold tracking-tight ${
              accent === "warning" ? "text-amber-500" : ""
            }`}
          >
            {value}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        </div>
        {icon && (
          <div className={`shrink-0 rounded-2xl p-3 ${accent === "warning" ? "bg-amber-500/10" : "bg-primary/10"}`}>
            {icon}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Página ─────────────────────────────────────────────────────────────────

export default function WhatsAppCostDashboard() {
  const [period, setPeriod] = useState<PeriodKey>("this_month");
  const [summary, setSummary] = useState<CostUsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [rates, setRates] = useState({ marketing: 0, utility: 0, authentication: 0, serviceProjected: 0 });
  const [savingRates, setSavingRates] = useState(false);

  const fetchSummary = useCallback(async (p: PeriodKey) => {
    setLoading(true);
    const { from, to } = getRange(p);
    const [data, err] = await whatsappService.getCostSummary(from.toISOString(), to.toISOString());
    setLoading(false);
    if (err || !data) {
      toast.error(err?.msg || "Erro ao buscar o resumo de custos");
      return;
    }
    setSummary(data);
    setRates({
      marketing: data.settings.marketing,
      utility: data.settings.utility,
      authentication: data.settings.authentication,
      serviceProjected: data.settings.serviceProjected,
    });
  }, []);

  useEffect(() => {
    void fetchSummary(period);
  }, [period, fetchSummary]);

  async function handleSaveRates() {
    setSavingRates(true);
    const [, err] = await whatsappService.updateCostSettings(rates);
    setSavingRates(false);
    if (err) {
      toast.error(err?.msg || "Erro ao salvar tarifas");
      return;
    }
    toast.success("Tarifas atualizadas");
    void fetchSummary(period);
  }

  const chartData =
    summary?.rows.map((r) => ({
      name: CATEGORY_LABEL[r.category],
      atual: Number(r.currentSubtotal.toFixed(4)),
      projetado: Number(r.projectedSubtotal.toFixed(4)),
    })) || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Custo de mensagens no WhatsApp</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Quanto o Baita Ticket está gastando com WhatsApp hoje — e o quanto deve custar depois
            que a Meta muda a cobrança em {PRICING_CHANGE_LABEL}.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5 rounded-lg border border-border p-1">
          {PERIODS.map((p) => (
            <Button
              key={p.key}
              size="sm"
              variant={period === p.key ? "default" : "ghost"}
              className="h-8"
              onClick={() => setPeriod(p.key)}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
        <AlertTriangle className="h-5 w-5 shrink-0 text-amber-500" />
        <div className="space-y-1.5 text-sm text-amber-700 dark:text-amber-400">
          <p>
            <strong>O que muda em {PRICING_CHANGE_LABEL}:</strong> hoje, toda mensagem de texto
            livre que um atendente manda para o cliente <strong>dentro da janela de 24h</strong>{" "}
            (mensagem de serviço) é <strong>grátis</strong>, não importa quantas você mande. A
            partir dessa data, a Meta passa a cobrar <strong>por mensagem enviada, mesmo dentro
            dessas 24h</strong> — não é uma cobrança única por conversa, é por cada mensagem.
          </p>
          <p className="text-xs opacity-90">
            Exemplo: um atendente que hoje manda 3 mensagens de texto livre para o mesmo cliente
            gasta R$ 0,00. Depois de {PRICING_CHANGE_LABEL}, essas mesmas 3 mensagens passam a
            custar cerca de {formatBRL(rates.serviceProjected * 3)} (estimativa). O valor oficial
            só sai até 01/09/2026 — até lá, a coluna "Projetado" é uma estimativa que você pode
            ajustar nas tarifas mais abaixo.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full rounded-2xl" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Mensagens enviadas"
              value={String(summary?.totalMessages ?? 0)}
              description="No período selecionado"
            />
            <StatCard
              title="Custo atual"
              value={formatBRL(summary?.currentTotal ?? 0)}
              description="Com a cobrança de hoje"
              icon={<TrendingUp className="h-5 w-5 text-primary" />}
            />
            <StatCard
              title="Custo projetado (pós-outubro)"
              value={formatBRL(summary?.projectedTotal ?? 0)}
              description="Mesmo volume, regra nova"
              icon={<AlertTriangle className="h-5 w-5 text-amber-500" />}
              accent="warning"
            />
            <StatCard
              title="Aumento estimado"
              value={formatBRL(summary?.increase ?? 0)}
              description={
                summary?.increasePct !== null && summary?.increasePct !== undefined
                  ? `+${summary.increasePct.toFixed(0)}% sobre o custo atual`
                  : "Sem custo atual para comparar"
              }
              accent="warning"
            />
          </div>

          <div className="rounded-2xl border border-border/50 bg-card p-6 shadow-sm">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Custo atual vs. projetado por categoria
            </h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted/50" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis
                    width={64}
                    tick={{ fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => formatBRL(v)}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    cursor={{ fill: "var(--muted)", opacity: 0.4 }}
                    formatter={(value: number) => formatBRL(value)}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="atual" name="Atual" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} maxBarSize={48} />
                  <Bar dataKey="projetado" name="Projetado (pós-outubro)" fill="#eb6834" radius={[6, 6, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-2xl border border-border/50 bg-card p-6 shadow-sm overflow-x-auto">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Detalhamento por categoria
            </h3>
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="pb-2 pr-4">Categoria</th>
                  <th className="pb-2 pr-4">Mensagens</th>
                  <th className="pb-2 pr-4">Tarifa atual</th>
                  <th className="pb-2 pr-4">Subtotal atual</th>
                  <th className="pb-2 pr-4">Tarifa projetada</th>
                  <th className="pb-2">Subtotal projetado</th>
                </tr>
              </thead>
              <tbody>
                {summary?.rows.map((r) => (
                  <tr key={r.category} className="border-b border-border/50 last:border-0">
                    <td className="py-2.5 pr-4">
                      <p className="font-medium">{CATEGORY_LABEL[r.category]}</p>
                      <p className="text-xs text-muted-foreground">{CATEGORY_HINT[r.category]}</p>
                    </td>
                    <td className="py-2.5 pr-4 tabular-nums">{r.count}</td>
                    <td className="py-2.5 pr-4 tabular-nums">{formatBRL(r.currentRate)}</td>
                    <td className="py-2.5 pr-4 tabular-nums font-medium">{formatBRL(r.currentSubtotal)}</td>
                    <td className="py-2.5 pr-4 tabular-nums">{formatBRL(r.projectedRate)}</td>
                    <td className="py-2.5 tabular-nums font-medium">{formatBRL(r.projectedSubtotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="rounded-2xl border border-border/50 bg-card p-6 shadow-sm">
        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Tarifas por mensagem (R$)
        </h3>
        <p className="mt-1 mb-4 text-sm text-muted-foreground">
          Esses valores são usados para calcular os custos acima. Ajuste aqui quando a Meta
          divulgar um preço diferente para a sua conta ou região.
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Marketing</Label>
            <Input
              type="number"
              step="0.001"
              min="0"
              value={rates.marketing}
              onChange={(e) => setRates((r) => ({ ...r, marketing: Number(e.target.value) }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Utilidade</Label>
            <Input
              type="number"
              step="0.001"
              min="0"
              value={rates.utility}
              onChange={(e) => setRates((r) => ({ ...r, utility: Number(e.target.value) }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Autenticação</Label>
            <Input
              type="number"
              step="0.001"
              min="0"
              value={rates.authentication}
              onChange={(e) => setRates((r) => ({ ...r, authentication: Number(e.target.value) }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Serviço — estimativa pós-outubro</Label>
            <Input
              type="number"
              step="0.001"
              min="0"
              value={rates.serviceProjected}
              onChange={(e) => setRates((r) => ({ ...r, serviceProjected: Number(e.target.value) }))}
            />
          </div>
        </div>
        <Button className="mt-4 gap-2" disabled={savingRates} onClick={() => void handleSaveRates()}>
          {savingRates && <Loader2 className="h-4 w-4 animate-spin" />}
          Salvar tarifas
        </Button>
      </div>
    </div>
  );
}
